// ============================================================
// 签到弹窗组件 — 深色科技风
// ============================================================

import { Modal, Button } from 'antd'
import {
  CheckCircleFilled,
  GiftOutlined,
  FireOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { SIGN_IN_REWARDS } from '@task-bazaar/shared'
import { useDeviceContext } from './DeviceProvider'
import type { UseSignInReturn } from '../hooks/useSignIn'

// ─── Props ──────────────────────────────────────────────────

interface SignInModalProps extends UseSignInReturn {}

// ─── 组件 ───────────────────────────────────────────────────

export default function SignInModal({
  isSigned,
  consecutiveDays,
  todayReward,
  signedDays,
  showModal,
  signIn,
  loading,
  closeModal,
}: SignInModalProps) {
  const { isMobile } = useDeviceContext()

  // 计算当前周期进度（7天一周期）
  const cycleDay = consecutiveDays % 7 || 7

  return (
    <Modal
      open={showModal}
      onCancel={closeModal}
      footer={null}
      centered
      width={isMobile ? '92vw' : 420}
      closable={false}
      maskClosable={!loading}
      className="sign-in-modal"
      styles={{
        mask: { backdropFilter: 'blur(4px)' },
        body: { padding: 0, overflow: 'hidden' },
        wrapper: { transition: 'none' },
        content: {
          borderRadius: 20,
          background: 'linear-gradient(180deg, #1A1A2E 0%, #16213E 100%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
        },
      }}
    >
        {/* 关闭按钮 */}
        <button
          onClick={closeModal}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
            e.currentTarget.style.transform = 'scale(1.1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
            e.currentTarget.style.transform = 'scale(1)'
          }}
        >
          <CloseOutlined style={{ color: '#A0A0B0', fontSize: 14 }} />
        </button>

        {/* 顶部装饰 */}
        <div
          style={{
            position: 'relative',
            padding: isMobile ? '40px 24px 32px' : '48px 32px 36px',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          {/* 背景光效 */}
          <div
            style={{
              position: 'absolute',
              top: '-50%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: isSigned
                ? 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%)',
              filter: 'blur(40px)',
              pointerEvents: 'none',
            }}
          />

          {/* 图标 */}
          <div
            style={{
              position: 'relative',
              width: 80,
              height: 80,
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: isSigned
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
              border: `2px solid ${isSigned ? 'rgba(16, 185, 129, 0.3)' : 'rgba(139, 92, 246, 0.3)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isSigned ? (
              <CheckCircleFilled style={{ fontSize: 40, color: '#10B981' }} />
            ) : (
              <GiftOutlined style={{ fontSize: 40, color: '#8B5CF6' }} />
            )}
          </div>

          {/* 标题 */}
          <h2
            style={{
              position: 'relative',
              fontSize: isMobile ? 22 : 24,
              fontWeight: 700,
              color: '#FFFFFF',
              marginBottom: 8,
            }}
          >
            {isSigned ? '今日已签到' : '每日签到'}
          </h2>

          {/* 描述 */}
          <p
            style={{
              position: 'relative',
              fontSize: 14,
              color: '#A0A0B0',
              marginBottom: 0,
            }}
          >
            {isSigned ? (
              <>
                已连续签到 <span style={{ color: '#10B981', fontWeight: 600 }}>{consecutiveDays}</span> 天，获得{' '}
                <span style={{ color: '#F59E0B', fontWeight: 600 }}>{todayReward}</span> 积分
              </>
            ) : (
              <>
                今日签到可得{' '}
                <span style={{ color: '#F59E0B', fontWeight: 700, fontSize: 18 }}>{todayReward}</span>{' '}
                积分
              </>
            )}
          </p>

          {/* 连续签到提示 */}
          {!isSigned && consecutiveDays > 0 && (
            <div
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 12,
                padding: '6px 14px',
                borderRadius: 20,
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
              }}
            >
              <FireOutlined style={{ color: '#F59E0B', fontSize: 14 }} />
              <span style={{ color: '#FCD34D', fontSize: 13, fontWeight: 500 }}>
                已连续签到 {consecutiveDays} 天
              </span>
            </div>
          )}
        </div>

        {/* 签到按钮 */}
        {!isSigned && (
          <div style={{ padding: '0 24px 24px' }}>
            <Button
              type="primary"
              block
              size="large"
              loading={loading}
              onClick={signIn}
              style={{
                height: 52,
                borderRadius: 14,
                fontSize: 16,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                border: 'none',
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
              }}
            >
              立即签到
            </Button>
          </div>
        )}

        {/* 签到奖励规则 */}
        <div
          style={{
            padding: isMobile ? '20px 16px' : '24px 24px',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <span style={{ color: '#6B6B80', fontSize: 13 }}>连续签到奖励</span>
            <span style={{ color: '#4A4A5A', fontSize: 11 }}>7天一周期</span>
          </div>

          {/* 奖励网格 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: isMobile ? 4 : 10,
              justifyContent: 'center',
            }}
          >
            {Object.entries(SIGN_IN_REWARDS).map(([day, reward]) => {
              const dayNum = Number(day)
              // 已签到：该天在 signedDays 中
              const isDaySigned = signedDays.includes(dayNum)
              // 未来天：大于当前周期天数
              const isFuture = dayNum > cycleDay

              return (
                <div
                  key={day}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: isMobile ? 4 : 6,
                    minWidth: 0,
                  }}
                >
                  {/* Day 标签 */}
                  <span
                    style={{
                      fontSize: isMobile ? 9 : 10,
                      color: '#4A4A5A',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                    }}
                  >
                    Day
                  </span>

                  {/* 数字圆圈 */}
                  <div
                    style={{
                      width: isMobile ? 32 : 40,
                      height: isMobile ? 32 : 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? 12 : 16,
                      fontWeight: 700,
                      fontFamily: "'JetBrains Mono', monospace",
                      transition: 'all 0.2s',
                      // 已签到 → 绿色渐变，未来 → 灰色，未签到当天 → 紫色渐变
                      background: isDaySigned
                        ? 'linear-gradient(135deg, #10B981, #059669)'
                        : isFuture
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                      border: isDaySigned
                        ? '2px solid rgba(16, 185, 129, 0.5)'
                        : isFuture
                        ? '2px solid rgba(255, 255, 255, 0.08)'
                        : '2px solid rgba(139, 92, 246, 0.5)',
                      color: isDaySigned || !isFuture
                        ? '#FFFFFF'
                        : '#6B6B80',
                      boxShadow: isDaySigned
                        ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                        : isFuture
                        ? 'none'
                        : '0 4px 12px rgba(139, 92, 246, 0.3)',
                    }}
                  >
                    {isDaySigned ? (
                      <CheckCircleFilled style={{ fontSize: isMobile ? 16 : 18 }} />
                    ) : (
                      dayNum
                    )}
                  </div>

                  {/* 积分奖励 */}
                  <span
                    style={{
                      fontSize: isMobile ? 12 : 13,
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: isDaySigned
                        ? '#10B981'
                        : isFuture
                        ? '#6B6B80'
                        : '#A78BFA',
                    }}
                  >
                    +{reward}
                  </span>
                </div>
              )
            })}
          </div>

          {/* 底部提示 */}
          <div
            style={{
              marginTop: 16,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(139, 92, 246, 0.08)',
              border: '1px solid rgba(139, 92, 246, 0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <FireOutlined style={{ color: '#8B5CF6', fontSize: 14 }} />
            <span style={{ color: '#8B5CF6', fontSize: 12 }}>
              连续签到7天可获得额外奖励，中断后重新计算
            </span>
          </div>
        </div>
    </Modal>
  )
}
